import { useEffect, useMemo, useState } from "react";
import {
  createRefundBatch,
  getRefundBatch,
  importBatchResponse,
  listPendingCardRefunds,
  listRefundBatches,
  refundBatchCsvUrl,
  type BatchRefund,
  type CardRefundPending,
  type RefundBatch,
} from "@/lib/api";
import { formatAmount, formatDate } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { DataTable, Badge, statusColor, type ColumnDef } from "@/components/DataTable";
import { AlertTriangle, CreditCard, Download, FileUp, Info, RefreshCw } from "lucide-react";

// Cf ADR-0020 + migration 018. Refunds carte processés en batch via Equity acquirer :
// - Onglet "À exporter" : refunds en processing sans batch → sélection + créer batch → CSV
// - Onglet "En attente Equity" : batchs exportés, on attend le CSV réponse
// - Onglet "Traités récents" : batchs closed/partially_processed 30 derniers jours
// - Onglet "Alertes SLA" : refunds qui ont dépassé leur deadline

type Tab = "pending" | "awaiting_equity" | "recent" | "alerts";

export function CardRefundBatchesPage() {
  const [tab, setTab] = useState<Tab>("pending");
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Data
  const [pending, setPending] = useState<CardRefundPending[]>([]);
  const [batches, setBatches] = useState<RefundBatch[]>([]);
  const [selectedRefundIds, setSelectedRefundIds] = useState<Set<string>>(new Set());
  const [selectedBatch, setSelectedBatch] = useState<{ batch: RefundBatch; refunds: BatchRefund[] } | null>(null);

  const [notice, setNotice] = useState<{ kind: "success" | "error"; message: string } | null>(null);

  useEffect(() => {
    setLoading(true);
    setNotice(null);
    if (tab === "pending") {
      listPendingCardRefunds()
        .then((r) => setPending(r.data))
        .catch((e) => setNotice({ kind: "error", message: `Erreur chargement : ${e.message}` }))
        .finally(() => setLoading(false));
    } else if (tab === "awaiting_equity") {
      listRefundBatches({ status: "exported" })
        .then((r) => setBatches(r.data))
        .catch((e) => setNotice({ kind: "error", message: e.message }))
        .finally(() => setLoading(false));
    } else if (tab === "recent") {
      Promise.all([
        listRefundBatches({ status: "closed", limit: 50 }),
        listRefundBatches({ status: "partially_processed", limit: 50 }),
      ])
        .then(([a, b]) => setBatches([...a.data, ...b.data]))
        .catch((e) => setNotice({ kind: "error", message: e.message }))
        .finally(() => setLoading(false));
    } else {
      // Alertes : refunds pending dont deadline dépassée
      listPendingCardRefunds()
        .then((r) => {
          const now = Date.now();
          const late = r.data.filter(
            (p) => p.internal_export_deadline && new Date(p.internal_export_deadline).getTime() < now,
          );
          setPending(late);
        })
        .catch((e) => setNotice({ kind: "error", message: e.message }))
        .finally(() => setLoading(false));
    }
  }, [tab, refreshKey]);

  const totalSelectedByCurrency = useMemo(() => {
    const map: Record<string, number> = {};
    for (const r of pending) {
      if (!selectedRefundIds.has(r.refund_id)) continue;
      map[r.currency] = (map[r.currency] ?? 0) + r.amount_minor;
    }
    return map;
  }, [pending, selectedRefundIds]);

  const handleToggleSelect = (id: string) => {
    setSelectedRefundIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedRefundIds.size === pending.length) setSelectedRefundIds(new Set());
    else setSelectedRefundIds(new Set(pending.map((p) => p.refund_id)));
  };

  const handleCreateBatch = async () => {
    if (selectedRefundIds.size === 0) return;
    if (!confirm(`Créer un batch avec ${selectedRefundIds.size} refund(s) ? Cette action verrouille les refunds pour l'envoi à Equity.`)) return;
    try {
      const batch = await createRefundBatch({ refund_ids: Array.from(selectedRefundIds) });
      setNotice({
        kind: "success",
        message: `Batch ${batch.id} créé (${batch.refund_count} refunds). Télécharge le CSV et envoie-le à Equity.`,
      });
      setSelectedRefundIds(new Set());
      setRefreshKey((k) => k + 1);
      // Aussi propose direct le download
      const csvPath = refundBatchCsvUrl(batch.id);
      window.location.href = `${import.meta.env.VITE_API_URL ?? ""}${csvPath}`;
    } catch (e) {
      const err = e as Error;
      setNotice({ kind: "error", message: `Erreur création batch : ${err.message}` });
    }
  };

  const openBatchDetail = async (batchId: string) => {
    try {
      const detail = await getRefundBatch(batchId);
      setSelectedBatch(detail);
    } catch (e) {
      const err = e as Error;
      setNotice({ kind: "error", message: err.message });
    }
  };

  const handleImportResponse = async (batchId: string, file: File) => {
    try {
      const result = await importBatchResponse(batchId, file);
      setNotice({
        kind: "success",
        message: `Réponse Equity importée : ${result.processed.succeeded} succeeded, ${result.processed.failed} failed, ${result.processed.skipped} skipped. Batch status = ${result.batch_status}.`,
      });
      setSelectedBatch(null);
      setRefreshKey((k) => k + 1);
    } catch (e) {
      const err = e as Error;
      setNotice({ kind: "error", message: `Import échoué : ${err.message}` });
    }
  };

  // ------ Columns ------
  const pendingCols: ColumnDef<CardRefundPending>[] = useMemo(
    () => [
      {
        key: "select",
        header: "",
        minWidth: 40,
        cell: (r) => (
          <input
            type="checkbox"
            checked={selectedRefundIds.has(r.refund_id)}
            onChange={() => handleToggleSelect(r.refund_id)}
          />
        ),
      },
      {
        key: "refund_id",
        header: "Refund",
        minWidth: 200,
        cell: (r) => (
          <div className="font-mono text-xs">
            <div className="text-[#181c32]">{r.refund_id}</div>
            <div className="text-[#7e8299]">{r.external_reference ?? "—"}</div>
          </div>
        ),
      },
      {
        key: "method",
        header: "Méthode",
        minWidth: 100,
        cell: (r) => (
          <div className="flex items-center gap-2">
            <CreditCard size={14} className="text-info-700" />
            <span className="text-xs uppercase font-semibold">{r.payment_method}</span>
          </div>
        ),
      },
      {
        key: "amount",
        header: "Montant",
        align: "right",
        minWidth: 120,
        cell: (r) => (
          <span className="font-bold text-[#181c32]">{formatAmount(r.amount_minor, r.currency)}</span>
        ),
      },
      {
        key: "customer",
        header: "Client",
        minWidth: 180,
        cell: (r) => (
          <div className="text-xs">
            <div>{r.customer_name || "—"}</div>
            <div className="text-[#7e8299]">{r.customer_email ?? ""}</div>
          </div>
        ),
      },
      {
        key: "created",
        header: "Créé",
        minWidth: 120,
        cell: (r) => <span className="text-xs text-[#7e8299]">{formatDate(r.created_at)}</span>,
      },
      {
        key: "deadline",
        header: "Deadline export",
        minWidth: 140,
        cell: (r) => {
          if (!r.internal_export_deadline) return <span className="text-[#c8c8d0]">—</span>;
          const late = new Date(r.internal_export_deadline).getTime() < Date.now();
          return (
            <span className={`text-xs ${late ? "text-danger-700 font-semibold" : "text-[#7e8299]"}`}>
              {late && <AlertTriangle size={12} className="inline mr-1" />}
              {formatDate(r.internal_export_deadline)}
            </span>
          );
        },
      },
    ],
    [selectedRefundIds],
  );

  const batchCols: ColumnDef<RefundBatch>[] = useMemo(
    () => [
      {
        key: "id",
        header: "Batch ID",
        minWidth: 180,
        cell: (b) => (
          <button
            className="font-mono text-xs text-brand-700 hover:underline"
            onClick={() => openBatchDetail(b.id)}
          >
            {b.id}
          </button>
        ),
      },
      {
        key: "generated_at",
        header: "Créé",
        minWidth: 120,
        cell: (b) => <span className="text-xs text-[#7e8299]">{formatDate(b.generated_at)}</span>,
      },
      {
        key: "count",
        header: "Refunds",
        align: "right",
        minWidth: 80,
        cell: (b) => <span className="font-semibold">{b.refund_count}</span>,
      },
      {
        key: "totals",
        header: "Montants",
        minWidth: 180,
        cell: (b) => (
          <div className="text-xs">
            {b.total_amount_minor_by_currency
              ? Object.entries(b.total_amount_minor_by_currency).map(([cur, amt]) => (
                  <div key={cur}>{formatAmount(amt, cur)}</div>
                ))
              : "—"}
          </div>
        ),
      },
      {
        key: "status",
        header: "Statut",
        minWidth: 130,
        cell: (b) => <Badge color={statusColor(b.status)}>{b.status}</Badge>,
      },
      {
        key: "actions",
        header: "Actions",
        minWidth: 200,
        cell: (b) => (
          <div className="flex gap-2">
            <a
              href={`${import.meta.env.VITE_API_URL ?? ""}${refundBatchCsvUrl(b.id)}`}
              className="btn btn-light btn-sm inline-flex items-center gap-1"
              onClick={(e) => {
                // Force auth via fetch not simple href → use apiRequest through hidden download
                e.preventDefault();
                downloadCsvWithAuth(b.id);
              }}
            >
              <Download size={14} /> CSV
            </a>
            {(b.status === "exported" || b.status === "partially_processed") && (
              <button className="btn btn-primary btn-sm" onClick={() => openBatchDetail(b.id)}>
                <FileUp size={14} className="inline mr-1" /> Importer
              </button>
            )}
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <div>
      <PageHeader
        title="Refunds Cartes — Batches Equity"
        breadcrumbs={[{ label: "Finance" }, { label: "Refunds cartes" }]}
      />

      <div className="rounded-xl bg-brand-50 border border-brand-100 p-4 mb-4 flex gap-3">
        <Info size={18} className="text-brand-700 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-[#3f4254] leading-relaxed">
          <div className="font-semibold text-brand-800 mb-1">Comment ça marche</div>
          Les refunds carte passent par notre acquirer Equity qui les traite en <strong>batch manuel</strong>.
          SLA : max 1 jour ouvré côté Moko pour exporter + 7 jours ouvrés côté Equity =
          <strong> 8 jours ouvrés max total</strong>.<br />
          Flow : (1) sélectionne les refunds pending, (2) crée un batch → CSV téléchargé auto,
          (3) envoie le CSV à Equity par email, (4) quand Equity répond, upload leur CSV réponse pour
          finaliser chaque refund.
        </div>
      </div>

      {notice && (
        <div
          className={`mb-4 p-3 rounded-lg text-sm ${
            notice.kind === "success"
              ? "bg-success-50 text-success-700 border border-success-200"
              : "bg-danger-50 text-danger-700 border border-danger-200"
          }`}
        >
          {notice.message}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 mb-4">
        {[
          { key: "pending" as Tab, label: "À exporter" },
          { key: "awaiting_equity" as Tab, label: "En attente Equity" },
          { key: "recent" as Tab, label: "Traités récents" },
          { key: "alerts" as Tab, label: "Alertes SLA" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
              tab === t.key
                ? "border-brand-700 text-brand-700"
                : "border-transparent text-[#7e8299] hover:text-[#181c32]"
            }`}
          >
            {t.label}
          </button>
        ))}
        <div className="flex-1" />
        <button
          onClick={() => setRefreshKey((k) => k + 1)}
          className="p-2 text-[#7e8299] hover:text-[#181c32]"
          title="Rafraîchir"
        >
          <RefreshCw size={16} />
        </button>
      </div>

      {/* Content */}
      {(tab === "pending" || tab === "alerts") && (
        <>
          {tab === "pending" && pending.length > 0 && (
            <div className="mb-3 flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <button className="btn btn-light btn-sm" onClick={handleSelectAll}>
                {selectedRefundIds.size === pending.length ? "Tout désélectionner" : "Tout sélectionner"}
              </button>
              <div className="text-sm text-[#3f4254]">
                <strong>{selectedRefundIds.size}</strong> sélectionné(s)
                {selectedRefundIds.size > 0 && (
                  <>
                    {" — "}
                    {Object.entries(totalSelectedByCurrency).map(([cur, amt]) => (
                      <span key={cur} className="mr-2">
                        {formatAmount(amt, cur)}
                      </span>
                    ))}
                  </>
                )}
              </div>
              <div className="flex-1" />
              <button
                className="btn btn-primary btn-sm"
                disabled={selectedRefundIds.size === 0}
                onClick={handleCreateBatch}
              >
                Créer batch Equity ({selectedRefundIds.size})
              </button>
            </div>
          )}
          <DataTable<CardRefundPending>
            title={tab === "alerts" ? "Refunds en retard d'export (SLA interne dépassée)" : "Refunds carte à exporter"}
            columns={pendingCols}
            data={pending}
            rowKey={(r) => r.refund_id}
            loading={loading}
            emptyMessage={
              tab === "alerts"
                ? "Aucune alerte SLA — tous les refunds sont dans les temps ✅"
                : "Aucun refund carte en attente d'export"
            }
          />
        </>
      )}

      {(tab === "awaiting_equity" || tab === "recent") && (
        <DataTable<RefundBatch>
          title={tab === "awaiting_equity" ? "Batches envoyés, en attente réponse Equity" : "Batches traités (30 derniers jours)"}
          columns={batchCols}
          data={batches}
          rowKey={(b) => b.id}
          loading={loading}
          emptyMessage="Aucun batch"
        />
      )}

      {/* Batch detail modal (simple inline) */}
      {selectedBatch && (
        <BatchDetailModal
          batch={selectedBatch.batch}
          refunds={selectedBatch.refunds}
          onClose={() => setSelectedBatch(null)}
          onImport={(file) => handleImportResponse(selectedBatch.batch.id, file)}
        />
      )}
    </div>
  );
}

function BatchDetailModal({
  batch,
  refunds,
  onClose,
  onImport,
}: {
  batch: RefundBatch;
  refunds: BatchRefund[];
  onClose: () => void;
  onImport: (file: File) => void;
}) {
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-xl font-bold">{batch.id}</h2>
            <div className="text-sm text-[#7e8299]">
              {batch.refund_count} refunds · statut {batch.status} · créé {formatDate(batch.generated_at)}
            </div>
          </div>
          <button onClick={onClose} className="text-2xl text-[#7e8299]">×</button>
        </div>

        {(batch.status === "exported" || batch.status === "partially_processed") && (
          <div className="mb-4 p-4 bg-brand-50 border border-brand-100 rounded-lg">
            <div className="font-semibold text-brand-800 mb-2">Importer la réponse Equity</div>
            <div className="text-xs text-[#3f4254] mb-3">
              Format CSV attendu : colonnes <code>refund_id, equity_status, equity_reference, equity_notes</code>.
              <br />equity_status = SUCCESS ou FAILED.
            </div>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
              className="mb-2"
            />
            <button
              className="btn btn-primary btn-sm"
              disabled={!uploadFile}
              onClick={() => uploadFile && onImport(uploadFile)}
            >
              <FileUp size={14} className="inline mr-1" /> Importer et finaliser
            </button>
          </div>
        )}

        <div className="mt-4">
          <div className="font-semibold mb-2">Refunds du batch</div>
          <table className="w-full text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left p-2">Refund ID</th>
                <th className="text-right p-2">Montant</th>
                <th className="text-left p-2">Status</th>
                <th className="text-left p-2">Equity ref</th>
                <th className="text-left p-2">Notes</th>
              </tr>
            </thead>
            <tbody>
              {refunds.map((r) => (
                <tr key={r.refund_id} className="border-b">
                  <td className="p-2 font-mono">{r.refund_id}</td>
                  <td className="p-2 text-right">{formatAmount(r.amount_minor, r.currency)}</td>
                  <td className="p-2"><Badge color={statusColor(r.status)}>{r.status}</Badge></td>
                  <td className="p-2 font-mono text-[#7e8299]">{r.acquirer_reference ?? "—"}</td>
                  <td className="p-2 text-[#7e8299]">{r.acquirer_response_notes ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Helper : télécharge le CSV en incluant le JWT dans l'header
async function downloadCsvWithAuth(batchId: string) {
  const token = (await import("@/lib/api")).getAccessToken();
  const url = `${import.meta.env.VITE_API_URL ?? ""}${refundBatchCsvUrl(batchId)}`;
  const resp = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!resp.ok) {
    alert(`Erreur téléchargement CSV : HTTP ${resp.status}`);
    return;
  }
  const blob = await resp.blob();
  const dlUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = dlUrl;
  a.download = `${batchId}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(dlUrl);
}
